import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  AnalyticsCommonTypes,
  PracticeAnalyticsAggregations,
  PracticeAnalyticsApi,
  PracticeAnalyticsTypes,
} from '@frontend/api-analytics';
import {
  AnalyticsHooks,
  ColumnHeaderInfoTip,
  InfoTipPopover,
  TabbedTable,
  UserCard,
  LocationChip,
} from '@frontend/analytics-shared';
import { processExportableData } from '@frontend/file-download-helper';
import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { TableColumnConfig, TableLoadingSkeleton, emptyStateGraphics } from '@frontend/design-system';
import { useShowBulkMessageButton } from '../../hooks/use-show-bulk-message-button';
import { trackingIds } from '../../tracking-ids';
import { drillDownTableSize } from '../../views/common-styles';
import { TableActions } from '../table-actions';
import { BulkMessageButton } from './bulk-message-button';
import { getLast24WeeksDates } from './helpers/get-last-24-weeks-dates';
import { collectPatientIds, deriveExportValue } from './helpers/patients';
import { usePatientsLastContacted } from './hooks/use-patients-last-contacted';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';
import { SmallModePatientsList } from './small-mode-patients-list';

type Tab = 'all' | 'perio' | 'overduePerio';

type Props = {
  data?: ReturnType<typeof PracticeAnalyticsAggregations.activePatientsScheduledDetails>;
  isExportEnabled?: boolean;
  isLoading?: boolean;
};

export const ActivePatientsUnscheduledTable = ({ data, isExportEnabled, isLoading }: Props) => {
  const { t } = useTranslation('analytics');
  const { filters, isDemoAccount } = usePracticeAnalyticsShallowStore('filters', 'isDemoAccount');
  const { locationNames } = AnalyticsHooks.useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });
  const { isSmallMode } = useIsSmallMode();
  const isBulkMessagingEnabled = useShowBulkMessageButton();

  // Collect all patients from all buckets and separate them into all and perio categories
  const patients = useMemo(() => {
    const perio: PracticeAnalyticsTypes.PatientInfo[] = [];
    const overduePerio: PracticeAnalyticsTypes.PatientInfo[] = [];

    const allPatients = [
      ...(data?.aggregatedData.unscheduledHygienePatients.buckets || []),
      ...(data?.aggregatedData.unscheduledNonhygienePatients.buckets || []),
    ]
      .map(({ persons }) => {
        if (persons) {
          persons.forEach((person) => {
            if (person.isPerio) {
              perio.push(person);

              if (person.dueDate && dayjs(person.dueDate).isBefore(dayjs(), 'day')) {
                overduePerio.push(person);
              }
            }
          });
        }
        return persons;
      })
      .flat() as PracticeAnalyticsTypes.PatientInfo[];

    return {
      all: allPatients,
      overduePerio,
      perio,
    };
  }, [data?.aggregatedData]);

  const lastContactedParams = useMemo(() => {
    const { startDate, endDate } = getLast24WeeksDates();

    return {
      endTime: endDate,
      patientIds: collectPatientIds(patients.all),
      startTime: startDate,
    };
  }, [patients.all]);

  const { isLoading: isLoadingLastContacted, lastContactedDates } = usePatientsLastContacted(lastContactedParams);

  const processExportableDataHandler = useCallback(
    (data: PracticeAnalyticsTypes.PatientInfo[]) =>
      // Translation is not needed as this is for export purpose
      processExportableData({
        columns: [
          'Patient Name',
          ...((filters.locations?.length || 0) > 1 ? ['Location Name'] : []),
          'Phone Number',
          'Email',
          'Gender',
          'Last Visit',
          'Last Contacted',
          'Smart Due Date',
        ],
        data,
        deriveExportValue: (params) =>
          deriveExportValue({
            ...params,
            lastContactedDates,
            locationNames,
          }),
      }),
    [filters.locations, lastContactedDates, locationNames]
  );

  const tabs = useMemo(
    () => ({
      all: {
        data: patients?.all || [],
        label: t('All'),
        trackingId: trackingIds.practiceAnalytics.allUnscheduledPatientsTab,
      },
      perio: {
        data: patients?.perio || [],
        label: t('Perio'),
        trackingId: trackingIds.practiceAnalytics.perioPatientsTab,
      },
      overduePerio: {
        data: patients?.overduePerio || [],
        label: t('Overdue Perio'),
        trackingId: trackingIds.practiceAnalytics.overduePerioPatientsTab,
      },
    }),
    [patients, t]
  );

  const smallModeTableProps: PracticeAnalyticsTypes.SmallModePatientsListProps<Tab> = {
    exportFileName: 'Active Unscheduled Patients', // Translation not needed
    initialTab: 'all',
    isBulkMessagingEnabled,
    isDemoAccount,
    isExportEnabled,
    isLoadingLastContacted,
    isLoadingPatients: isLoading,
    processExportableData: processExportableDataHandler,
    tabs,
  };

  const largeModeTableProps: PracticeAnalyticsTypes.LargeModeTableProps<Tab> = {
    ...smallModeTableProps,
    lastContactedDates,
    locationNames,
    multiLocations: (filters.locations?.length || 0) > 1,
  };

  return isSmallMode ? (
    <SmallModePatientsList<Tab> {...smallModeTableProps} trackingIdBase='active-unscheduled-patients-small-mode' />
  ) : (
    <LargeModeTable {...largeModeTableProps} />
  );
};

const LargeModeTable = ({
  isBulkMessagingEnabled,
  isDemoAccount,
  isExportEnabled,
  isLoadingLastContacted,
  isLoadingPatients,
  lastContactedDates,
  locationNames,
  multiLocations,
  processExportableData,
  tabs,
}: PracticeAnalyticsTypes.LargeModeTableProps<Tab>) => {
  const { t } = useTranslation('analytics');

  const colConfig: TableColumnConfig<PracticeAnalyticsTypes.PatientInfo>[] = useMemo(() => {
    return [
      {
        Header: t('Patient Name'),
        headerLabel: t('Patient Name'),
        accessor: ({ FirstName, LastName, PreferredName, id }) =>
          JSON.stringify({
            firstName: FirstName,
            lastName: LastName,
            preferredName: PreferredName,
            userId: id,
          }),
        cellRenderer: (value: string) => {
          const props = JSON.parse(value);
          return <UserCard {...props} key={props.userId} openProfileOnClick={!isDemoAccount} showOnlyName />;
        },
        id: 'name',
        sticky: 'left' as AnalyticsCommonTypes.Alignment,
        width: 250,
      },
      {
        accessor: ({ locationId }) => locationId,
        cellRenderer: (locationId: string) => {
          return locationId ? (
            <LocationChip locationName={locationNames[locationId] || locationId} maxWidth={180} />
          ) : (
            '-'
          );
        },
        disableSortBy: true,
        Header: t('Location'),
        id: 'locationName',
        omit: !multiLocations,
        width: 220,
      },
      {
        Header: t('Phone Number'),
        headerLabel: t('Phone Number'),
        accessor: ({ HomePhone, MobilePhone }) => MobilePhone || HomePhone,
        cellRenderer: (value: string) => (value ? formatPhoneNumber(value) : '-'),
        id: 'phone-number',
        width: 180,
      },
      {
        Header: t('Gender'),
        headerLabel: t('Gender'),
        accessor: ({ Gender }) => Gender || '-',
        id: 'gender',
        width: 130,
      },
      {
        Header: t('Last Visit'),
        headerLabel: t('Last Visit'),
        accessor: ({ lastVisitDate }) => lastVisitDate,
        cellRenderer: (value: string) => formatters.date(value),
        id: 'lastVisitDate',
        width: 150,
      },
      {
        Header: (
          <ColumnHeaderInfoTip
            columnTitle={t('Last Contacted')}
            infoTip={
              <InfoTipPopover>
                {t('Date and Time of last outbound answered call to patient phone number')}
              </InfoTipPopover>
            }
          />
        ),
        headerLabel: t('Last Contacted'),
        accessor: ({ id }) => formatters.date(lastContactedDates?.[id]),
        cellRenderer: (lastContacted: string) => (isLoadingLastContacted ? <TableLoadingSkeleton /> : lastContacted),
        id: 'lastContacted',
        width: 180,
      },
      {
        Header: t('Due Date'),
        headerLabel: t('Due Date'),
        accessor: ({ dueDate }) => dueDate,
        cellRenderer: (value: string) => formatters.date(value),
        id: 'dueDate',
        width: 200,
      },
      {
        disableSortBy: true,
        Header: t('Actions'),
        headerLabel: t('Actions'),
        headerAlign: 'center' as AnalyticsCommonTypes.Alignment | 'center',
        accessor: (patient) => patient,
        cellRenderer: (patient) => {
          const phoneNumber = patient.MobilePhone || patient.HomePhone;
          return (
            <TableActions
              isDemoAccount={isDemoAccount}
              personId={patient.id}
              personName={patient.FirstName}
              phoneNumber={phoneNumber}
              trackingIdBase='active-unscheduled-patient'
            />
          );
        },
        id: 'actions',
        sticky: 'right' as AnalyticsCommonTypes.Alignment,
        width: 110,
      },
    ];
  }, [t, multiLocations, isDemoAccount, locationNames, lastContactedDates, isLoadingLastContacted]);

  const tabsCommonConfig = {
    colConfig,
    emptyStateConfig: 'users' as keyof typeof emptyStateGraphics,
  };

  return (
    <TabbedTable<PracticeAnalyticsTypes.PatientInfo>
      actions={BulkMessageButton}
      autorizedToExport={PracticeAnalyticsApi.auditPracticeAnalyticsExport}
      isDemoAccount={isDemoAccount}
      exportFileName='Active Unscheduled Patients' // Translation not needed
      initialTab='all'
      isExportReady={!isLoadingPatients && !isLoadingLastContacted}
      isLoading={isLoadingPatients}
      isSelectable={isBulkMessagingEnabled}
      processExportableData={processExportableData}
      showExportIcon={isExportEnabled}
      tabs={{
        all: {
          ...tabsCommonConfig,
          ...tabs.all,
          tableInstanceId: 'all-unscheduled-patients',
        },
        perio: {
          ...tabsCommonConfig,
          ...tabs.perio,
          tableInstanceId: 'perio-patients',
        },
        overduePerio: {
          ...tabsCommonConfig,
          ...tabs.overduePerio,
          tableInstanceId: 'overdue-perio-patients',
        },
      }}
      trackingIdBase='unscheduled-patients'
      wrapperStyle={drillDownTableSize}
    />
  );
};
